import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Lock, Loader2, Shield, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.success && res.data.token) {
        localStorage.setItem('adminToken', res.data.token);
        toast.success("Connexion réussie");
        navigate('/admin/dashboard');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-6">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
        </div>
      </header>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-3xl p-8 md:p-12 max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-lagoon-50 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-lagoon" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-lagoon-900 mb-2">
            Administration
          </h1>
          <p className="text-gray-600">
            Connectez-vous pour accéder au tableau de bord
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="admin-login-form">
          <div className="mb-4">
            <Label htmlFor="username" className="text-lagoon-900 font-semibold">
              Identifiant
            </Label>
            <div className="relative mt-2">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="pl-12 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                placeholder="Entrez l'identifiant"
                data-testid="username-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <Label htmlFor="password" className="text-lagoon-900 font-semibold">
              Mot de passe
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-12 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                placeholder="Entrez le mot de passe"
                data-testid="password-input"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="btn-coral w-full h-12"
            disabled={loading}
            data-testid="login-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Connexion...
              </>
            ) : (
              "Se connecter"
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="text-lagoon hover:text-lagoon-600 text-sm"
            data-testid="back-home-link"
          >
            Retour à l'accueil
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
